import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { 
  Upload, 
  X, 
  Send, 
  CheckCircle, 
  User, 
  Car, 
  Target, 
  Calendar,
  DollarSign,
  MessageSquare,
  Image as ImageIcon
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { toast } from 'react-hot-toast';
import { trackQuoteRequest } from '../lib/analytics';
import { addDocument } from '../lib/firestore-helpers';
import { notifyRequest } from '../lib/notify';

// Form validation schema
const schema = yup.object().shape({
  firstName: yup.string().required('Prénom requis'),
  lastName: yup.string().required('Nom requis'),
  email: yup.string().email('Email invalide').required('Email requis'),
  phone: yup.string().required('Téléphone requis'),
  vehicleBrand: yup.string().required('Marque du véhicule requise'),
  vehicleModel: yup.string().required('Modèle du véhicule requis'),
  vehicleYear: yup.number().min(1900, 'Année invalide').max(new Date().getFullYear(), 'Année invalide').required('Année requise'),
  projectType: yup.string().required('Type de projet requis'),
  usage: yup.string().required('Usage prévu requis'),
  budget: yup.string().required('Budget indicatif requis'),
  timeline: yup.string().required('Délai souhaité requis'),
  message: yup.string().required('Description du projet requise').min(50, 'Description trop courte (minimum 50 caractères)')
});

interface FormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  vehicleBrand: string;
  vehicleModel: string;
  vehicleYear: number;
  projectType: string;
  usage: string;
  budget: string;
  timeline: string;
  message: string;
}

interface UploadedFile {
  id: string;
  file: File;
  preview: string;
  name: string;
  size: number;
}

const CustomRequestPage: React.FC = () => {
  const { t } = useLanguage();
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<FormData>({
    resolver: yupResolver(schema)
  });

  const projectTypes = [
    { value: 'track-prep', label: 'Préparation circuit' },
    { value: 'restoration', label: 'Restauration véhicule classique' },
    { value: 'luxury-adaptation', label: 'Adaptation véhicule de luxe' },
    { value: 'american-import', label: 'Véhicule américain' },
    { value: 'custom-repair', label: 'Réparation sur-mesure' },
    { value: 'other', label: 'Autre projet' }
  ];

  const usageTypes = [
    { value: 'road', label: 'Route uniquement' },
    { value: 'track-occasional', label: 'Route + circuit occasionnel' },
    { value: 'track-frequent', label: 'Route + circuit fréquent' },
    { value: 'track-only', label: 'Circuit uniquement' },
    { value: 'show', label: 'Concours/Exposition' },
    { value: 'collection', label: 'Collection' }
  ];

  const budgetRanges = [
    { value: '1000-2500', label: '1 000€ - 2 500€' },
    { value: '2500-5000', label: '2 500€ - 5 000€' },
    { value: '5000-10000', label: '5 000€ - 10 000€' },
    { value: '10000-20000', label: '10 000€ - 20 000€' },
    { value: '20000+', label: 'Plus de 20 000€' },
    { value: 'to-discuss', label: 'À discuter' }
  ];

  const timelineOptions = [
    { value: 'urgent', label: 'Urgent (moins de 2 semaines)' },
    { value: '1-month', label: '1 mois' },
    { value: '2-3-months', label: '2-3 mois' },
    { value: '3-6-months', label: '3-6 mois' },
    { value: '6-months+', label: 'Plus de 6 mois' },
    { value: 'flexible', label: 'Flexible' }
  ];

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        toast.error(`Le fichier ${file.name} est trop volumineux (max 10MB)`);
        return;
      }

      if (!file.type.startsWith('image/')) {
        toast.error(`Le fichier ${file.name} n'est pas une image`);
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const newFile: UploadedFile = {
          id: Date.now() + Math.random().toString(36).substr(2, 9),
          file,
          preview: e.target?.result as string,
          name: file.name,
          size: file.size
        };

        setUploadedFiles(prev => [...prev, newFile]);
      };
      reader.readAsDataURL(file);
    });

    // Reset input
    event.target.value = '';
  };

  const removeFile = (fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId));
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    // Track quote request
    trackQuoteRequest(data.projectType, {
      vehicleBrand: data.vehicleBrand,
      vehicleModel: data.vehicleModel,
      vehicleYear: data.vehicleYear,
      budget: data.budget,
      timeline: data.timeline,
      usage: data.usage,
      hasFiles: uploadedFiles.length > 0,
      filesCount: uploadedFiles.length
    });
    
    try {
      const formPayload: Record<string, unknown> = { ...data };
      if (uploadedFiles.length > 0) {
        formPayload.images = uploadedFiles.map(f => ({
          name: f.name,
          size: f.size,
          base64: f.preview,
        }));
      }

      await addDocument('requests', {
        type: 'custom' as const,
        data: formPayload,
        status: 'new' as const,
        notes: '',
      });

      const { images, ...notifyData } = formPayload;
      notifyRequest('custom', notifyData as Record<string, unknown>);
      toast.success('Votre demande a ete envoyee avec succes !');
      setIsSubmitted(true);
      reset();
      setUploadedFiles([]);

    } catch (error) {
      toast.error('Erreur lors de l\'envoi. Veuillez reessayer.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-24 min-h-screen bg-gray-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, type: "spring", damping: 25 }}
          className="max-w-2xl mx-auto px-4 text-center"
        >
          <motion.div
            animate={{ 
              scale: [1, 1.2, 1],
              rotate: [0, 360, 720]
            }}
            transition={{ duration: 2, ease: "easeInOut" }}
            className="w-32 h-32 mx-auto mb-8 bg-green-100 rounded-full flex items-center justify-center"
          >
            <CheckCircle className="w-16 h-16 text-green-600" />
          </motion.div>
          
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            Demande envoyée !
          </h2>
          
          <p className="text-xl text-gray-600 mb-8">
            Nous avons bien reçu votre demande de projet sur-mesure. Notre équipe vous recontactera dans les 24 heures pour discuter des détails.
          </p>
          
          <motion.a
            href="/"
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="inline-flex items-center space-x-2 px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-all duration-200 shadow-lg hover:shadow-xl"
          >
            <span>Retour à l'accueil</span>
          </motion.a>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="pt-24 min-h-screen bg-gray-50">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-br from-black via-gray-900 to-black text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-5xl md:text-7xl font-bold mb-6">
              Projet Sur-Mesure
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-4xl mx-auto">
              Décrivez-nous votre projet et nous créerons une solution parfaitement adaptée à vos besoins
            </p>
            <div className="w-24 h-1 bg-gradient-to-r from-red-600 to-red-500 mx-auto mt-8 rounded-full" />
          </motion.div>
        </div>
      </section>

      {/* Form Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-12">
            {/* Contact Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Vos coordonnées</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Prénom *
                  </label>
                  <input
                    type="text"
                    {...register('firstName')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.firstName ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder="Votre prénom"
                  />
                  {errors.firstName && (
                    <p className="mt-2 text-sm text-red-600">{errors.firstName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom *
                  </label>
                  <input
                    type="text"
                    {...register('lastName')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.lastName ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder="Votre nom"
                  />
                  {errors.lastName && (
                    <p className="mt-2 text-sm text-red-600">{errors.lastName.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    {...register('email')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.email ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder="votre@email.com"
                  />
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Téléphone *
                  </label>
                  <input
                    type="tel"
                    {...register('phone')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.phone ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder="+352 XX XX XX XX"
                  />
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-600">{errors.phone.message}</p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Vehicle Information */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <Car className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Votre véhicule</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Marque *
                  </label>
                  <input
                    type="text"
                    {...register('vehicleBrand')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.vehicleBrand ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder="ex: Porsche"
                  />
                  {errors.vehicleBrand && (
                    <p className="mt-2 text-sm text-red-600">{errors.vehicleBrand.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Modèle *
                  </label>
                  <input
                    type="text"
                    {...register('vehicleModel')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.vehicleModel ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder="ex: 911 GT3"
                  />
                  {errors.vehicleModel && (
                    <p className="mt-2 text-sm text-red-600">{errors.vehicleModel.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Année *
                  </label>
                  <input
                    type="number"
                    {...register('vehicleYear')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.vehicleYear ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                    placeholder="2023"
                    min="1900"
                    max={new Date().getFullYear()}
                  />
                  {errors.vehicleYear && (
                    <p className="mt-2 text-sm text-red-600">{errors.vehicleYear.message}</p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Project Details */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <Target className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Détails du projet</h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de projet *
                  </label>
                  <select
                    {...register('projectType')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.projectType ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                  >
                    <option value="">Sélectionnez un type</option>
                    {projectTypes.map((type) => (
                      <option key={type.value} value={type.value}>
                        {type.label}
                      </option>
                    ))}
                  </select>
                  {errors.projectType && (
                    <p className="mt-2 text-sm text-red-600">{errors.projectType.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Usage prévu *
                  </label>
                  <select
                    {...register('usage')}
                    className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                      errors.usage ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                    }`}
                  >
                    <option value="">Sélectionnez un usage</option>
                    {usageTypes.map((usage) => (
                      <option key={usage.value} value={usage.value}>
                        {usage.label}
                      </option>
                    ))}
                  </select>
                  {errors.usage && (
                    <p className="mt-2 text-sm text-red-600">{errors.usage.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Budget indicatif *
                  </label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <select
                      {...register('budget')}
                      className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.budget ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                      }`}
                    >
                      <option value="">Sélectionnez un budget</option>
                      {budgetRanges.map((budget) => (
                        <option key={budget.value} value={budget.value}>
                          {budget.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.budget && (
                    <p className="mt-2 text-sm text-red-600">{errors.budget.message}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Délai souhaité *
                  </label>
                  <div className="relative">
                    <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <select
                      {...register('timeline')}
                      className={`w-full pl-12 pr-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        errors.timeline ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                      }`}
                    >
                      <option value="">Sélectionnez un délai</option>
                      {timelineOptions.map((timeline) => (
                        <option key={timeline.value} value={timeline.value}>
                          {timeline.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  {errors.timeline && (
                    <p className="mt-2 text-sm text-red-600">{errors.timeline.message}</p>
                  )}
                </div>
              </div>
            </motion.div>

            {/* Message */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <MessageSquare className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Description du projet</h2>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Décrivez-nous votre projet en détail *
                </label>
                <textarea
                  {...register('message')}
                  rows={6}
                  className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors resize-none ${
                    errors.message ? 'border-red-500 focus:border-red-500' : 'border-gray-200 focus:border-red-500'
                  }`}
                  placeholder="Décrivez vos objectifs, vos attentes, les modifications souhaitées, votre expérience, etc. Plus vous êtes précis, mieux nous pourrons vous conseiller."
                />
                {errors.message && (
                  <p className="mt-2 text-sm text-red-600">{errors.message.message}</p>
                )}
                <p className="mt-2 text-sm text-gray-500">Minimum 50 caractères</p>
              </div>
            </motion.div>

            {/* File Upload */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="bg-white rounded-2xl shadow-lg p-8"
            >
              <div className="flex items-center space-x-3 mb-6">
                <div className="w-10 h-10 bg-red-600 rounded-lg flex items-center justify-center">
                  <ImageIcon className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900">Photos du véhicule</h2>
                <span className="text-sm text-gray-500">(Optionnel)</span>
              </div>

              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-red-400 transition-colors">
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handleFileUpload}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  <Upload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Cliquez pour ajouter des photos
                  </p>
                  <p className="text-sm text-gray-500">
                    PNG, JPG jusqu'à 10MB par fichier
                  </p>
                </label>
              </div>

              {/* File Preview */}
              <AnimatePresence>
                {uploadedFiles.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="mt-6"
                  >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {uploadedFiles.map((file) => (
                        <motion.div
                          key={file.id}
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="relative group rounded-lg overflow-hidden bg-gray-100"
                        >
                          <img
                            src={file.preview}
                            alt={file.name}
                            className="w-full h-24 object-cover"
                          />
                          <button
                            type="button"
                            onClick={() => removeFile(file.id)}
                            className="absolute top-2 right-2 p-1 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white p-2">
                            <p className="text-xs truncate">{file.name}</p>
                            <p className="text-xs text-gray-300">
                              {(file.size / 1024 / 1024).toFixed(1)} MB
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>

            {/* Submit Button */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="text-center"
            >
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={!isSubmitting ? { scale: 1.02, y: -2 } : {}}
                whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                className={`inline-flex items-center space-x-3 px-12 py-4 font-semibold text-lg rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl ${
                  isSubmitting
                    ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-gray-600 border-t-transparent rounded-full"
                    />
                    <span>Envoi en cours...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5" />
                    <span>Envoyer ma demande</span>
                  </>
                )}
              </motion.button>
              
              <p className="mt-4 text-sm text-gray-500">
                Nous vous recontacterons dans les 24 heures
              </p>
            </motion.div>
          </form>
        </div>
      </section>
    </div>
  );
};

export default CustomRequestPage;